import { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Formik, Form, Field } from "formik";
import { Col, Container, Row, Button, FormGroup } from "reactstrap";
import { ReactstrapInput, ReactstrapSelect } from "reactstrap-formik";

import * as Yup from "yup";

import FormHelpers from "../../helpers/FormHelpers";
import ScrollToFieldError from "./Validation/ScrollToFieldError";
import CardPreview from "../Cards/CardPreview";
import AddressBook from "../AddressBook/AddressBook";

import { configuratorActions } from "../../store/configurator-slice";
import { cardPreviewActions } from "../../store/card-preview-slice";
import { addressBookActions } from "../../store/address-book-slice";
import { fetchConfiguratorData } from "../../store/configurator-actions";

function RecipientForm(props) {
  const dispatch = useDispatch();

  // get current request state
  const formStep = useSelector((state) => state.configurator.FormStep);
  const currentRequest = useSelector(
    (state) => state.configurator.ConfiguratorRequest
  );
  const requestDetails = currentRequest.DonorRequest;

  // set sendTo state
  const [sendToValue, setSendToValue] = useState(
    requestDetails.SendTo ? requestDetails.SendTo : "billing"
  );

  const isLoggedIn = currentRequest.DonorIsLoggedIn;

  // get title list
  const titles = currentRequest.Lists.DonorTitles;
  const titlesArray = titles.map((item) => {
    return item.Value;
  });

  // get country list
  const countries = currentRequest.Lists.Countries;
  const countriesArray = countries.map((item) => {
    return item.Value;
  });

  // get state list
  const states = currentRequest.Lists.States;
  const statesArray = states.map((item) => {
    return item.Value;
  });

  // get province list
  const provinces = currentRequest.Lists.Provinces;
  const provincesArray = provinces.map((item) => {
    return item.Value;
  });

  const [showAddressBook, setShowAddressBook] = useState(false);

  // init form validation schema
  const formSchema = Yup.object().shape({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    address1: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
    state: Yup.string().when("country", (country) => {
      if (country === "USA") return Yup.string().required("Required");
    }),
    province: Yup.string().when("country", (country) => {
      if (country === "Canada") return Yup.string().required("Required");
    }),
    city: Yup.string().required("Required"),
    postalCode: Yup.string().when("country", (country) => {
      if (country === "USA" || country === "Canada") {
        if(country === "USA") {
          return Yup.string().required("Required").matches(/^[0-9]{5}(?:-[0-9]{4})?$/, "Must be a valid zip code");
        } else {
          return Yup.string().required("Required").matches(/^(?!.*[DFIOQU])[A-VXY][0-9][A-Z][ -]?[0-9][A-Z][0-9]$/, "Must be a valid postal code");
        }
      }
    }),
  });

  // set sendTo address state
  const [sendToAddress, setSendToAddress] = useState({
    RecipientTitle: requestDetails.RecipientTitle,
    RecipientFirstName: requestDetails.RecipientFirstName,
    RecipientLastName: requestDetails.RecipientLastName,
    RecipientAddress1: requestDetails.RecipientAddress1,
    RecipientAddress2: requestDetails.RecipientAddress2,
    RecipientCountry: (requestDetails.RecipientCountry) ? requestDetails.RecipientCountry : 'USA',
    RecipientStateProvince: requestDetails.RecipientStateProvince,
    RecipientCity: requestDetails.RecipientCity,
    RecipientZipPostalCode: requestDetails.RecipientZipPostalCode,
  });

  useEffect(() => {
    if(isLoggedIn && requestDetails.CardTypeValue === "sendtome" && sendToValue === 'billing' && !requestDetails.RecipientFirstName) {
      setSendToAddress({
        RecipientTitle: currentRequest.DonorInfo.Title,
        RecipientFirstName: currentRequest.DonorInfo.FirstName,
        RecipientLastName: currentRequest.DonorInfo.LastName,
        RecipientAddress1: currentRequest.DonorInfo.Address1,
        RecipientAddress2: currentRequest.DonorInfo.Address2,
        RecipientCountry: currentRequest.DonorInfo.Country,
        RecipientStateProvince: currentRequest.DonorInfo.StateProvince,
        RecipientCity: currentRequest.DonorInfo.City,
        RecipientZipPostalCode: currentRequest.DonorInfo.PostalCode
      });
    }
  }, []);

  useEffect(() => {
    if(isLoggedIn && requestDetails.CardTypeValue === "sendtome" && sendToValue === 'billing' && !requestDetails.RecipientFirstName) {
      setSendToAddress({
        RecipientTitle: currentRequest.DonorInfo.Title,
        RecipientFirstName: currentRequest.DonorInfo.FirstName,
        RecipientLastName: currentRequest.DonorInfo.LastName,
        RecipientAddress1: currentRequest.DonorInfo.Address1,
        RecipientAddress2: currentRequest.DonorInfo.Address2,
        RecipientCountry: currentRequest.DonorInfo.Country,
        RecipientStateProvince: currentRequest.DonorInfo.StateProvince,
        RecipientCity: currentRequest.DonorInfo.City,
        RecipientZipPostalCode: currentRequest.DonorInfo.PostalCode
      });
    }
  }, [isLoggedIn]);

  const sendToClickHandler = (e) => {
    setSendToValue(e.target.value);
    if(e.target.value === 'custom') {
      setSendToAddress({
        RecipientTitle: '',
        RecipientFirstName: '',
        RecipientLastName: '',
        RecipientAddress1: '',
        RecipientAddress2: '',
        RecipientCountry: '',
        RecipientStateProvince: '',
        RecipientCity: '',
        RecipientZipPostalCode: ''
      });
    } else {
      setSendToAddress({
        RecipientTitle: currentRequest.DonorInfo.Title,
        RecipientFirstName: currentRequest.DonorInfo.FirstName,
        RecipientLastName: currentRequest.DonorInfo.LastName,
        RecipientAddress1: currentRequest.DonorInfo.Address1,
        RecipientAddress2: currentRequest.DonorInfo.Address2,
        RecipientCountry: currentRequest.DonorInfo.Country,
        RecipientStateProvince: currentRequest.DonorInfo.StateProvince,
        RecipientCity: currentRequest.DonorInfo.City,
        RecipientZipPostalCode: currentRequest.DonorInfo.PostalCode
      });
    }
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const formSubmitHandler = (values) => {
    dispatch(configuratorActions.setRecipient(values));
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  };

  const previousClickHandler = (values) => {
    dispatch(configuratorActions.setFormStep("Personalize"));
    dispatch(cardPreviewActions.setFieldFocus(""));
    FormHelpers.scrollToTop();
  };

  const inputFocusHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus("envelope-recipient"));
  };

  const inputBlurHandler = (e) => {
    dispatch(cardPreviewActions.setFieldFocus(""));
  };

  const formRef = useRef();
  const addressUpdateHandler = (e) => {
    let values = {...formRef.current.values};
    values[e.target.name] = e.target.value;

    let envelopeValues = {...values};

    if(values.country === 'USA') {
      envelopeValues.state = FormHelpers.getStateProvinceCode(states, values.state);
    } else if(values.country === 'Canada') {
      envelopeValues.province = FormHelpers.getStateProvinceCode(provinces, values.province);
    }

    dispatch(cardPreviewActions.setEnvelopeRecipientAddress(envelopeValues));
  };

  const addressUpdateFromAddressBookHandler = (address) => {

    setSendToValue('custom');

    setSendToAddress({
      RecipientTitle: address.Title,
      RecipientFirstName: address.FirstName,
      RecipientLastName: address.LastName,
      RecipientAddress1: address.Address1,
      RecipientAddress2: address.Address2,
      RecipientCountry: address.Country,
      RecipientStateProvince: address.StateProvince,
      RecipientCity: address.City,
      RecipientZipPostalCode: address.ZipPostalCode
    });

    let previewValues = {};
    previewValues.title = address.Title;
    previewValues.firstName = address.FirstName;
    previewValues.lastName = address.LastName;
    previewValues.address1 = address.Address1;
    previewValues.address2 = address.Address2;
    previewValues.country = address.Country;
    previewValues.state = address.StateProvince;
    previewValues.province = address.StateProvince;
    previewValues.city = address.City;
    previewValues.postalCode = address.ZipPostalCode;

    if(address.Country === 'USA') {
      previewValues.state = FormHelpers.getStateProvinceCode(states, address.StateProvince);
    } else if(address.Country === 'Canada') {
      previewValues.province = FormHelpers.getStateProvinceCode(provinces, address.StateProvince);
    }

    dispatch(
      cardPreviewActions.setEnvelopeRecipientAddress(previewValues)
    );
    toggleAddressBook(false);
  };

  function toggleAddressBook(show) {
    setShowAddressBook(show);
  }

  function addToAddressBookClickHandler(values) {
    let address = {
      Title: values.title,
      FirstName: values.firstName,
      LastName: values.lastName,
      Address1: values.address1,
      Address2: values.address2,
      Country: values.country,
      StateProvince: '',
      City: values.city,
      ZipPostalCode: values.postalCode
    };

    if(address.Country === 'USA') {
      address.StateProvince = values.state;
    } else if(address.Country === 'Canada') {
      address.StateProvince = values.province;
    }

    address.UserId = currentRequest.DonorInfo.Id;

    dispatch(addressBookActions.setAddingAddress(address));
    setShowAddressBook(true);
  }

  return (
    <div className="cfg-input-form cfg-recipient" data-step="recipient">
      <Formik
        enableReinitialize={true}
        initialValues={{
          sendTo: sendToValue,
          title: sendToAddress.RecipientTitle,
          firstName: sendToAddress.RecipientFirstName,
          lastName: sendToAddress.RecipientLastName,
          address1: sendToAddress.RecipientAddress1,
          address2: sendToAddress.RecipientAddress2,
          country: (sendToAddress.RecipientCountry) ? sendToAddress.RecipientCountry : 'USA',
          state: sendToAddress.RecipientStateProvince,
          province: sendToAddress.RecipientStateProvince,
          city: sendToAddress.RecipientCity,
          postalCode: sendToAddress.RecipientZipPostalCode,
        }}
        validationSchema={formSchema}
        onSubmit={formSubmitHandler}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setFieldTouched,
        }) => (
          <Form className="text-start">
            <ScrollToFieldError />
            <Container className="cfg-form-section">
              <Row className="mobile-only">
                <Col xs="12">
                  <CardPreview
                    zoom={0.38}
                    showCard={false}
                    showEnvelope={true}
                    preview={true}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col xs="12" className="text-center">
                  <Button
                    type="button"
                    size="lg"
                    color="primary"
                    onClick={() => toggleAddressBook(true)}
                  >
                    Display Address Book
                  </Button>
                </Col>
              </Row>

              {isLoggedIn && requestDetails.CardTypeValue === "sendtome" && (
                <Row>
                  <Col xs="12">
                    <FormGroup>
                      <div className="form-check">
                        <label>
                          <Field
                            type="radio"
                            name="sendTo"
                            value="billing"
                            onClick={sendToClickHandler}
                          />
                          My billing name and address
                        </label>
                      </div>
                      <div className="form-check">
                        <label>
                          <Field
                            type="radio"
                            name="sendTo"
                            value="custom"
                            onClick={sendToClickHandler}
                          />
                          The name and address below
                        </label>
                      </div>
                      {errors.sendTo && touched.sendTo && (
                        <div className="invalid-feedback">{errors.sendTo}</div>
                      )}
                    </FormGroup>
                  </Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col xs="12">
                  <label htmlFor="title">Title:</label>
                  <Field
                    name="title"
                    component={ReactstrapSelect}
                    onChange={(e) => {
                      handleChange(e);
                      addressUpdateHandler(e);
                    }}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                    inputprops={{
                      name: "title",
                      id: "title",
                      options: titlesArray,
                      defaultOption: "Select...",
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="firstName">
                    First Name:
                  </label>
                  <Field
                    name="firstName"
                    id="firstName"
                    maxlength="30"
                    component={ReactstrapInput}
                    onKeyUp={addressUpdateHandler}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      inputBlurHandler(e);
                      handleBlur(e);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="lastName">
                    Last Name:
                  </label>
                  <Field
                    name="lastName"
                    id="lastName"
                    maxlength="30"
                    component={ReactstrapInput}
                    onKeyUp={addressUpdateHandler}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="address1">
                    Address 1:
                  </label>
                  <Field
                    name="address1"
                    id="address1"
                    maxlength="50"
                    component={ReactstrapInput}
                    onKeyUp={addressUpdateHandler}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label htmlFor="address2">Address 2:</label>
                  <Field
                    name="address2"
                    id="address2"
                    maxlength="50"
                    component={ReactstrapInput}
                    onKeyUp={addressUpdateHandler}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                  />
                </Col>
              </Row>
              <Row className="mb-2">
                <Col xs="12">
                  <label className="required" htmlFor="country">
                    Country:
                  </label>
                  <Field
                    name="country"
                    component={ReactstrapSelect}
                    inputprops={{
                      name: "country",
                      id: "country",
                      options: countriesArray,
                      defaultOption: "Select...",
                    }}
                    onChange={(e) => {
                      handleChange(e);
                      addressUpdateHandler(e);
                    }}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                  />
                </Col>
              </Row>
              {values.country === "USA" && (
                <Row className="mb-2">
                  <Col xs="12">
                    <label
                      className={values.country === "USA" ? "required" : ""}
                      htmlFor="state"
                    >
                      State:
                    </label>
                    <Field
                      name="state"
                      component={ReactstrapSelect}
                      inputprops={{
                        name: "state",
                        id: "state",
                        options: statesArray,
                        defaultOption: "Select...",
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        addressUpdateHandler(e);
                      }}
                      onFocus={inputFocusHandler}
                      onBlur={(e) => {
                        handleBlur(e);
                        inputBlurHandler(e);
                      }}
                    />
                  </Col>
                </Row>
              )}

              {values.country === "Canada" && (
                <Row className="mb-2">
                  <Col xs="12">
                    <label
                      className={values.country === "Canada" ? "required" : ""}
                      htmlFor="province"
                    >
                      Province:
                    </label>
                    <Field
                      name="province"
                      component={ReactstrapSelect}
                      inputprops={{
                        name: "province",
                        id: "province",
                        options: provincesArray,
                        defaultOption: "Select...",
                      }}
                      onChange={(e) => {
                        handleChange(e);
                        addressUpdateHandler(e);
                      }}
                      onFocus={inputFocusHandler}
                      onBlur={(e) => {
                        handleBlur(e);
                        inputBlurHandler(e);
                      }}
                    />
                  </Col>
                </Row>
              )}
              <Row className="mb-2">
                <Col xs="6">
                  <label className="required" htmlFor="city">
                    City:
                  </label>
                  <Field
                    name="city"
                    id="city"
                    maxlength="50"
                    component={ReactstrapInput}
                    onKeyUp={addressUpdateHandler}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                  />
                </Col>
                <Col xs="6">
                  <label
                    className={
                      values.country === "USA" || values.country === "Canada"
                        ? "required"
                        : ""
                    }
                    htmlFor="postalCode"
                  >
                    Zip/Postal Code:
                  </label>
                  <Field
                    name="postalCode"
                    id="postalCode"
                    maxlength="10"
                    component={ReactstrapInput}
                    onKeyUp={addressUpdateHandler}
                    onFocus={inputFocusHandler}
                    onBlur={(e) => {
                      handleBlur(e);
                      inputBlurHandler(e);
                    }}
                  />
                </Col>
              </Row>
              {(Object.keys(touched).length > 0 && Object.keys(errors).length === 0) && (
                <Row className="mb-2">
                  <Col xs="12" className="text-center">
                    <a onClick={() => addToAddressBookClickHandler(values)}>Add Recipient to My Address Book</a>
                  </Col>
                </Row>
              )}
            </Container>
            <Button
              type="button"
              size="sm"
              color="primary"
              onClick={previousClickHandler}
              className="btn-secondary"
            >
              Previous
            </Button>{" "}
            <Button
              type="submit"
              size="sm"
              color="primary"
              className="float-end"
            >
              Next
            </Button>
          </Form>
        )}
      </Formik>
      {showAddressBook && (
        <AddressBook
          formStep={formStep}
          showAddressBook={showAddressBook}
          onToggleAddressBookClickHandler={toggleAddressBook}
          onSelectClickHandler={addressUpdateFromAddressBookHandler}
        />
      )}
    </div>
  );
}

export default RecipientForm;
